/**
 * Mapping Workspace — SHAPE ingestion ISOLATED, FLAG-GATED mock.
 *
 * Enabled ONLY when VUE_APP_SHAPE_MOCK === "true". The store never falls back
 * to this mock on an API error — it is used exclusively when the explicit
 * development flag is on.
 *
 * It returns contract-compatible `{ data: { success, ... } }` envelopes for the
 * SHAPE-relevant Mapping Workspace reads (application-workspace, application
 * questions) and the new SHAPE fetch/refresh endpoint, mirroring the Readiness
 * API response shape the store expects. Delete this file + drop the flag when
 * the real endpoints are wired — no other change is required.
 */

/* eslint-disable no-use-before-define */

export function isShapeMockEnabled() {
  return process.env.VUE_APP_SHAPE_MOCK === "true";
}

function clone(value) {
  return structuredClone(value);
}

/* ------------------------------------------------------------------ */
/* Seed data                                                           */
/* ------------------------------------------------------------------ */

// A SHAPE-sourced application and a legacy uploaded one on the same product,
// so the UI's SHAPE labelling and application distinction can be exercised.
function seedApplications() {
  return [
    {
      entity_id: "app_shape_001",
      name: "Cyber SHAPE Application",
      application_filename: "cyber-shape-2026.json",
      source: "shape",
      source_label: "SHAPE",
      shape_application_id: "SHAPE-APP-7781",
      has_scanned_data: true,
      question_count: 5,
      last_synced_at: "2026-07-20T10:15:00Z",
    },
    {
      entity_id: "app_upload_001",
      name: "Legacy Uploaded Application",
      application_filename: "legacy-upload.csv",
      source: "upload",
      source_label: "Uploaded",
      has_scanned_data: true,
      question_count: 2,
      last_synced_at: null,
    },
  ];
}

// Questions for the SHAPE application, covering every SHAPE mapping state and
// carrying element_key (technical) + a backend canonical suggestion.
function seedShapeQuestions() {
  return [
    {
      id: "SQ-1001",
      element_key: "cyber.mfa.enabled",
      category: "Access Control",
      text: "Is multi-factor authentication enforced for all remote access?",
      answerType: "Yes/No",
      answerOptions: ["Yes", "No"],
      source: "shape",
      mappingStatus: "suggested",
      mappedTo: null,
      mappingId: null,
      canonicalQuestionId: null,
      draftCanonicalQuestionId: null,
      suggestionCanonicalQuestionId: "4",
      suggestionCanonicalKey: "security.mfa_enabled",
      suggestionCanonicalLabel: "MFA enabled?",
      suggestionConfidence: 0.92,
    },
    {
      id: "SQ-1002",
      element_key: "cyber.mfa.coverage_pct",
      category: "Access Control",
      text: "What percentage of accounts are protected by MFA?",
      answerType: "Percentage",
      answerOptions: [],
      source: "shape",
      mappingStatus: "confirmed",
      mappedTo: "security.mfa_coverage",
      mappingId: "MAP-2001",
      canonicalQuestionId: "5",
      draftCanonicalQuestionId: null,
      suggestionCanonicalQuestionId: "5",
      suggestionCanonicalKey: "security.mfa_coverage",
      suggestionCanonicalLabel: "MFA coverage",
      suggestionConfidence: 0.88,
    },
    {
      id: "SQ-1003",
      element_key: "company.industry_code",
      category: "Company Info",
      text: "What is your primary industry classification?",
      answerType: "Text",
      answerOptions: [],
      source: "shape",
      mappingStatus: "overridden",
      mappedTo: "company.industry",
      mappingId: "MAP-2002",
      canonicalQuestionId: "3",
      draftCanonicalQuestionId: null,
      suggestionCanonicalQuestionId: "99",
      suggestionCanonicalKey: "company.sector",
      suggestionCanonicalLabel: "Sector (suggested)",
      suggestionConfidence: 0.61,
    },
    {
      id: "SQ-1004",
      element_key: "cyber.edr.vendor",
      category: "Network Security",
      text: "Which endpoint detection & response vendor do you use?",
      answerType: "Text",
      answerOptions: [],
      source: "shape",
      mappingStatus: "unmapped",
      mappedTo: null,
      mappingId: null,
      canonicalQuestionId: null,
      draftCanonicalQuestionId: null,
      suggestionCanonicalQuestionId: null,
      suggestionCanonicalKey: null,
      suggestionCanonicalLabel: null,
      suggestionConfidence: null,
    },
    {
      id: "SQ-1005",
      element_key: "meta.internal_ref",
      category: "Company Info",
      text: "Internal broker reference code (not customer facing).",
      answerType: "Text",
      answerOptions: [],
      source: "shape",
      mappingStatus: "ignored",
      mappedTo: null,
      mappingId: "MAP-2003",
      canonicalQuestionId: null,
      draftCanonicalQuestionId: null,
      suggestionCanonicalQuestionId: null,
      suggestionCanonicalKey: null,
      suggestionCanonicalLabel: null,
      suggestionConfidence: null,
    },
  ];
}

function seedUploadQuestions() {
  return [
    {
      id: "UQ-1",
      element_key: null,
      category: "Company Info",
      text: "Company legal name",
      answerType: "Text",
      answerOptions: [],
      source: "upload",
      mappingStatus: "Mapped",
      mappedTo: "company.legal_name",
      mappingId: "MAP-9001",
      canonicalQuestionId: "1",
      suggestionCanonicalQuestionId: "1",
      suggestionCanonicalKey: "company.legal_name",
    },
    {
      id: "UQ-2",
      element_key: null,
      category: "Company Info",
      text: "Headquarters address",
      answerType: "Address",
      answerOptions: [],
      source: "upload",
      mappingStatus: "Needs Mapping",
      mappedTo: null,
      mappingId: null,
      canonicalQuestionId: null,
      suggestionCanonicalQuestionId: null,
    },
  ];
}

const QUESTIONS_BY_APP = {
  app_shape_001: seedShapeQuestions,
  app_upload_001: seedUploadQuestions,
};

function applicationById(id) {
  return seedApplications().find((a) => a.entity_id === id) || null;
}

/* ------------------------------------------------------------------ */
/* Request dispatcher                                                  */
/* ------------------------------------------------------------------ */

function ok(payload = {}) {
  return { data: { success: true, ...payload } };
}
function fail(message) {
  return { data: { success: false, message } };
}

const ROUTES = [
  {
    method: "GET",
    re: /\/admin\/mapping-workspace\/products\/([^/]+)\/application-workspace$/,
    handler: () => ok({ assigned: seedApplications(), available: [] }),
  },
  {
    method: "GET",
    re: /\/admin\/mapping-workspace\/products\/([^/]+)\/applications\/([^/]+)\/questions$/,
    handler: (m) => {
      const applicationId = m[2];
      const app = applicationById(applicationId);
      const builder = QUESTIONS_BY_APP[applicationId];
      if (!app || !builder) return fail(`No mock application ${applicationId}`);
      return ok({ questions: builder(), application: clone(app) });
    },
  },
  {
    method: "POST",
    re: /\/admin\/mapping-workspace\/products\/([^/]+)\/applications\/refresh$/,
    handler: () =>
      ok({
        assigned: seedApplications(),
        available: [],
        message: "SHAPE applications refreshed from the Readiness API (mock).",
      }),
  },
];

export function mockShapeRequest(method, path, body = {}) {
  // Store callers pass lowercase methods ("get"/"post"); routes use "GET"/"POST".
  const normalizedMethod = String(method).toUpperCase();
  const route = ROUTES.find((r) => r.method === normalizedMethod && r.re.test(path));
  if (!route) {
    return Promise.resolve(fail(`No SHAPE mock route for ${normalizedMethod} ${path}`));
  }
  return Promise.resolve(route.handler(path.match(route.re), body || {}));
}
